import React, { useState, useEffect } from 'react'
import { Calendar, Plus, Trash2, Loader2, AlertCircle, CheckCircle, Pencil, X, Check } from 'lucide-react'
import { supabase } from '../../lib/supabase'

interface PublicHoliday {
  id: string
  date: string
  name: string
  created_at: string
}

export function PublicHolidaysManagement() {
  const [holidays, setHolidays] = useState<PublicHoliday[]>([])
  const [filteredHolidays, setFilteredHolidays] = useState<PublicHoliday[]>([])
  const [availableYears, setAvailableYears] = useState<string[]>([])
  const [selectedYear, setSelectedYear] = useState<string>('all')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [newDate, setNewDate] = useState('')
  const [newName, setNewName] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editDate, setEditDate] = useState('')
  const [editName, setEditName] = useState('')
  const [editSaving, setEditSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  useEffect(() => {
    fetchHolidays()
  }, [])

  useEffect(() => {
    const years = [...new Set(holidays.map(h => h.date.substring(0, 4)))].sort()
    setAvailableYears(years)

    if (selectedYear === 'all') {
      setFilteredHolidays(holidays)
    } else {
      setFilteredHolidays(holidays.filter(h => h.date.startsWith(selectedYear)))
    }
  }, [holidays, selectedYear])

  const fetchHolidays = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('public_holidays')
        .select('*')
        .order('date', { ascending: true })

      if (error) throw error
      setHolidays(data || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement')
    } finally {
      setLoading(false)
    }
  }

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg)
    setTimeout(() => setSuccessMessage(null), 3000)
  }

  const isDateDuplicate = (date: string, excludeId?: string) => {
    return holidays.some(h => h.date === date && h.id !== excludeId)
  }

  const addHoliday = async () => {
    if (!newDate || !newName.trim()) {
      setError('Veuillez remplir la date et le nom du jour férié.')
      return
    }

    if (isDateDuplicate(newDate)) {
      setError(`Un jour férié existe déjà pour le ${formatDate(newDate)}. Modifiez l'entrée existante si nécessaire.`)
      return
    }

    setSaving(true)
    setError(null)

    try {
      const { error } = await supabase
        .from('public_holidays')
        .insert({ date: newDate, name: newName.trim() })

      if (error) {
        if (error.code === '23505') {
          throw new Error(`Un jour férié existe déjà pour cette date.`)
        }
        throw error
      }

      setNewDate('')
      setNewName('')
      showSuccess('Jour férié ajouté avec succès !')
      await fetchHolidays()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'ajout")
    } finally {
      setSaving(false)
    }
  }

  const startEdit = (holiday: PublicHoliday) => {
    setEditingId(holiday.id)
    setEditDate(holiday.date)
    setEditName(holiday.name)
    setError(null)
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditDate('')
    setEditName('')
  }

  const saveEdit = async (id: string) => {
    if (!editDate || !editName.trim()) {
      setError('Veuillez remplir la date et le nom.')
      return
    }

    if (isDateDuplicate(editDate, id)) {
      setError(`Un autre jour férié existe déjà pour le ${formatDate(editDate)}.`)
      return
    }

    setEditSaving(true)
    setError(null)

    try {
      const { error } = await supabase
        .from('public_holidays')
        .update({ date: editDate, name: editName.trim() })
        .eq('id', id)

      if (error) {
        if (error.code === '23505') {
          throw new Error('Un jour férié existe déjà pour cette date.')
        }
        throw error
      }

      cancelEdit()
      showSuccess('Jour férié modifié avec succès !')
      await fetchHolidays()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la modification')
    } finally {
      setEditSaving(false)
    }
  }

  const deleteHoliday = async (id: string, name: string) => {
    if (!confirm(`Supprimer le jour férié "${name}" ?`)) return

    try {
      const { error } = await supabase
        .from('public_holidays')
        .delete()
        .eq('id', id)

      if (error) throw error

      showSuccess('Jour férié supprimé.')
      await fetchHolidays()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la suppression')
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00')
    return date.toLocaleDateString('fr-BE', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Jours fériés</h1>
        <p className="text-gray-600">
          Gérez les jours fériés pour désactiver automatiquement la saisie des présences ces jours-là.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-700">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="ml-3 text-red-400 hover:text-red-600">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
          <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
          <p className="text-green-700">{successMessage}</p>
        </div>
      )}

      {/* Add new holiday */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Ajouter un jour férié</h2>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date"
              value={newDate}
              onChange={(e) => { setNewDate(e.target.value); setError(null) }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex-[2]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addHoliday()}
              placeholder="ex: Fête nationale belge"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={addHoliday}
              disabled={saving}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              <span>Ajouter</span>
            </button>
          </div>
        </div>
      </div>

      {/* List with year filter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Calendar className="h-5 w-5 mr-2" />
            Jours fériés ({filteredHolidays.length}{selectedYear !== 'all' ? ` en ${selectedYear}` : ''})
          </h2>

          {/* Year filter */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Année :</span>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setSelectedYear('all')}
                className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                  selectedYear === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Toutes
              </button>
              {availableYears.map(year => (
                <button
                  key={year}
                  onClick={() => setSelectedYear(year)}
                  className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                    selectedYear === year
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
            <span className="ml-3 text-gray-600">Chargement...</span>
          </div>
        ) : filteredHolidays.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Calendar className="mx-auto h-12 w-12 text-gray-300 mb-3" />
            <p>Aucun jour férié{selectedYear !== 'all' ? ` pour ${selectedYear}` : ''}.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredHolidays.map((holiday) => (
              <div key={holiday.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                {editingId === holiday.id ? (
                  /* Edit mode */
                  <div className="flex items-center gap-3 flex-wrap">
                    <input
                      type="date"
                      value={editDate}
                      onChange={(e) => { setEditDate(e.target.value); setError(null) }}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit(holiday.id)
                        if (e.key === 'Escape') cancelEdit()
                      }}
                      className="flex-1 min-w-[200px] px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      autoFocus
                    />
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => saveEdit(holiday.id)}
                        disabled={editSaving}
                        className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        {editSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        <span>Confirmer</span>
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="flex items-center space-x-1 px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300 transition-colors"
                      >
                        <X className="h-4 w-4" />
                        <span>Annuler</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Display mode */
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{holiday.name}</p>
                      <p className="text-sm text-blue-600">{formatDate(holiday.date)}</p>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => startEdit(holiday)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Modifier"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => deleteHoliday(holiday.id, holiday.name)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
